import { useEffect } from 'react';

interface UseAndroidKeyboardDetectionOptions {
  enabled?: boolean;
  onKeyboardClose?: () => void;
}

/**
 * Hook to detect Android keyboard close via visualViewport resize events.
 * Prevents duplicate state transitions from multiple resize events.
 * 
 * @param options.enabled - Whether the detection is enabled (default: true)
 * @param options.onKeyboardClose - Callback when keyboard closes
 */
export const useAndroidKeyboardDetection = ({
  enabled = true,
  onKeyboardClose,
}: UseAndroidKeyboardDetectionOptions) => {
  useEffect(() => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    if (!enabled || !isAndroid || !window.visualViewport || !onKeyboardClose) {
      return;
    }

    let keyboardOpen = false;
    let lastIsOpen: boolean | null = null;

    const handleAndroidKeyboard = () => {
      const heightDiff = window.innerHeight - window.visualViewport!.height;
      const isOpen = heightDiff > 100; // threshold

      // Prevent duplicate state transitions
      if (lastIsOpen === isOpen) return;
      lastIsOpen = isOpen;

      // Keyboard just closed
      if (keyboardOpen && !isOpen) {
        onKeyboardClose();
      }

      keyboardOpen = isOpen;
    };

    window.visualViewport.addEventListener('resize', handleAndroidKeyboard, { passive: true });

    return () => {
      window.visualViewport!.removeEventListener('resize', handleAndroidKeyboard);
    };
  }, [enabled, onKeyboardClose]);
};
